'use client';

import { useRef, useState } from 'react';
import type * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { UseCanvas } from '../UseCanvas';
import { DebugScene, DebugSceneProps } from '../_helpers/DebugScene';
import { useFrameRectAndProgressTracker } from '../../_hooks/useFrameRectAndProgressTracker';
import { roundedLerp } from '../../_utils/roundedLerp';
import { round } from '../../_utils/round';

/**
 * Generic THREE.js Scene that tracks the dimensions and position of a DOM element while scrolling with optional debugging scene.
 * Scene is positioned and scaled exactly above DOM element. This component renders a 3D scene using UseCanvas and View components.
 *
 * @param {Object} props - The props of the the HTML wrapper element.
 * @param {React.ReactNode} props.children - The content to be rendered within the fixed view section.
 * @param {DebugSceneProps} [props.debug] - The props for the DebugScene component to enable debugging scene.
 * @returns {JSX.Element} - The JSX element representing the fixed view section.
 */

type ScrollSceneProps = {
	debug?: DebugSceneProps;
	sceneAspectRatio?: { width: number; height: number };
	children?(sceneProps: any): JSX.Element;
};

function ScrollScene({
	style,
	children,
	sceneAspectRatio,
	debug,
	...divProps
}: Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'children'> & ScrollSceneProps): JSX.Element {
	const wrapperRef = useRef<HTMLDivElement>(null);

	return (
		<>
			<div
				data-three-js-layout='scroll-scene'
				// className='border'
				ref={wrapperRef}
				style={{
					width: '100%',
					height: '100%',
					top: 0,
					left: 0,
					...style,
				}}
				{...divProps}
			/>
			<UseCanvas>
				<SceneGroup debug={debug} wrapperRef={wrapperRef} sceneAspectRatio={sceneAspectRatio}>
					{children}
				</SceneGroup>
			</UseCanvas>
		</>
	);
}

function SceneGroup(props: ScrollSceneProps & { wrapperRef: React.RefObject<HTMLDivElement> }) {
	const groupRef = useRef<THREE.Group>(null);

	const scrollSceneAnimationControls = useOnScrollPosition(groupRef, props.wrapperRef, props.sceneAspectRatio);

	return (
		<group ref={groupRef}>
			{(!props.children || props.debug) && <DebugScene {...props.debug} scrollSceneAnimationControls={scrollSceneAnimationControls} />}
			{props.children?.({ scrollSceneAnimationControls })}
		</group>
	);
}

ScrollScene.displayName = 'ScrollScene';

export { ScrollScene };

export function useOnScrollPosition(
	groupRef: React.RefObject<THREE.Group>,
	wrapperRef: React.RefObject<HTMLElement>,
	sceneAspectRatio?: { width: number; height: number },
) {
	const [tracker, setTracker] = useState(wrapperRef.current);
	const onScrollSceneAnimation = useRef<OnScrollSceneAnimation>({
		animating: true,
		scrollAnimation: true,
		smoothPositionUpdate: 'none',
		tracker: 'default',
	}).current;
	const { rect, observer } = useFrameRectAndProgressTracker(tracker, undefined, sceneAspectRatio, onScrollSceneAnimation.scrollAnimation);

	useFrame((_, delta) => {
		if (groupRef.current && window) {
			if (onScrollSceneAnimation.animating) {
				if (onScrollSceneAnimation.smoothPositionUpdate !== 'none') {
					const positionX = roundedLerp(groupRef.current.position.x, observer.positionX, delta * 2, 2);
					if (round(groupRef.current.position.x, 2) !== positionX) {
						groupRef.current.position.x = positionX;
						if (onScrollSceneAnimation.smoothPositionUpdate === 'waiting for start')
							onScrollSceneAnimation.smoothPositionUpdate = 'waiting for finish';
					} else groupRef.current.position.x = observer.positionX;

					const positionY = roundedLerp(groupRef.current.position.y, observer.positionY, delta * 2, 2);
					if (round(groupRef.current.position.y, 2) !== positionY) {
						groupRef.current.position.y = positionY;
						if (onScrollSceneAnimation.smoothPositionUpdate === 'waiting for start')
							onScrollSceneAnimation.smoothPositionUpdate = 'waiting for finish';
					} else groupRef.current.position.y = observer.positionY;

					const scaleX = roundedLerp(groupRef.current.scale.x, rect.threeJsRects.sceneScale, delta * 2, 2);
					if (scaleX !== groupRef.current.scale.x) {
						groupRef.current.scale.set(scaleX, scaleX, scaleX);
						if (onScrollSceneAnimation.smoothPositionUpdate === 'waiting for start')
							onScrollSceneAnimation.smoothPositionUpdate = 'waiting for finish';
					} else groupRef.current.scale.set(rect.threeJsRects.sceneScale, rect.threeJsRects.sceneScale, rect.threeJsRects.sceneScale);

					if (
						groupRef.current.position.x === observer.positionX &&
						groupRef.current.position.y === observer.positionY &&
						groupRef.current.scale.x === rect.threeJsRects.sceneScale &&
						onScrollSceneAnimation.smoothPositionUpdate === 'waiting for finish'
					)
						onScrollSceneAnimation.smoothPositionUpdate = 'none';
				} else {
					groupRef.current.position.x = observer.positionX;
					groupRef.current.position.y = observer.positionY;
					groupRef.current.scale.set(rect.threeJsRects.sceneScale, rect.threeJsRects.sceneScale, rect.threeJsRects.sceneScale);
				}
			}
		}
	});

	return {
		onScrollSceneAnimation,
		setAllowAnimation: (value: boolean) => {
			onScrollSceneAnimation.animating = value;
		},
		setAllowScrollAnimation: (value: boolean) => {
			onScrollSceneAnimation.scrollAnimation = value;
		},
		setSmoothPositioning: (value: OnScrollSceneAnimation['smoothPositionUpdate']) => {
			onScrollSceneAnimation.smoothPositionUpdate = value;
		},
		setNewTrackerWrapper: (value: React.RefObject<HTMLElement>) => {
			setTracker(value.current);
			onScrollSceneAnimation.tracker = 'custom';
		},
		resetTrackerToDefault: () => {
			setTracker(wrapperRef.current);
			onScrollSceneAnimation.tracker = 'default';
		},
	};
}
type OnScrollSceneAnimation = {
	animating: boolean;
	scrollAnimation: boolean;
	smoothPositionUpdate: 'waiting for finish' | 'waiting for start' | 'none';
	tracker: 'default' | 'custom';
};

export type ScrollSceneAnimationControls = { scrollSceneAnimationControls: ReturnType<typeof useOnScrollPosition> };
